import {createFileRoute} from '@tanstack/react-router'
import TournamentLiveView from '../../tournaments/TournamentLiveView'
import services from '../../services'
import {config} from '../../config/config'
import {humanizeSlug} from '../../utilities'
import {isFieldSport} from '../../fieldRounds/api'
import {FieldLeaderboardLive} from '../../fieldRounds/FieldLeaderboardPublic'
import {useSports} from '../../sports/registry'

export const Route = createFileRoute('/live/tournaments/$tournament_uid')({
    component: RouteComponent,
    // Optional ?division=<key> selects a single division's live view.
    validateSearch: (search: Record<string, unknown>): {division?: string} => ({
        division: typeof search.division === 'string' ? search.division : undefined,
    }),
    // Fetch the tournament server-side so the title/description render in the SSR HTML (SEO).
    loader: async ({params}) => {
        try {
            const t = await services.tournaments.find(params.tournament_uid)
            return {event: t && (t as any).uid ? t : null}
        } catch {
            return {event: null}
        }
    },
    head: ({loaderData, params}) => {
        const e: any = (loaderData as any)?.event
        const name = e?.title || humanizeSlug(params.tournament_uid)
        const isEsports = e?.sport_type === 'esports'
        const sport = e?.sport_type ? String(e.sport_type).replace(/_/g, ' ') : 'sports'
        const slug = e?.slug || params.tournament_uid
        const url = `${config.publicUrl}/live/tournaments/${slug}/`
        const title = `${name} | Live ${isEsports ? 'Bracket' : 'Tournament'} | ScorePal`
        // Esports talks about matches and games, never courts or sets.
        const description = isEsports
            ? `Follow ${name} live on ScorePal: the bracket, every match result, standings and the road to the grand final.`
            : `Follow ${name} live on ScorePal. Real-time ${sport} tournament scores, brackets, standings, and results.`
        // A shared bracket link needs a picture to be worth tapping on Discord, WhatsApp or X.
        // The event's own card image first, then its banner or badge, then the ScorePal card —
        // never nothing, which is what these links previewed with before.
        const image: string = e?.public_card_image_url || e?.banner_url || e?.logo_url || `${config.publicUrl}/og-image.png`
        return {
            meta: [
                {title},
                {name: 'description', content: description},
                {property: 'og:type', content: 'website'},
                {property: 'og:site_name', content: 'ScorePal'},
                {property: 'og:title', content: title},
                {property: 'og:description', content: description},
                {property: 'og:url', content: url},
                {property: 'og:image', content: image},
                {name: 'twitter:card', content: 'summary_large_image'},
                {name: 'twitter:title', content: title},
                {name: 'twitter:description', content: description},
                {name: 'twitter:image', content: image},
            ],
            links: [{rel: 'canonical', href: url}],
        }
    },
})

function RouteComponent() {
    // The loader already fetched the tournament (for SSR meta). Seeding the live view with it
    // collapses the client's request waterfall: without this, the page re-ran tournaments-find
    // and only THEN could stages + players start — one full round trip of dead time on a page
    // people open mid-event.
    const {event} = Route.useLoaderData()
    useSports() // the live registry decides whether this is a field event (Prompt 8)
    // Golf and ten-pin with multi-sport on: a leaderboard of rounds, not groups and a bracket.
    if (event && isFieldSport((event as any).sport_type)) return <FieldLeaderboardLive event={event} />
    return <TournamentLiveView initialEvent={event} />
}
